<template>
  <div>
    <div class="overdraftFacilityQuestion">
      <input-radio-group
        id="overdraft-facility"
        v-model="application._extension.haveOverdraftFacility"
        :options="overdraftFacilityOptions"
        v-validation.noEvent="['requiredBoolean']"
      >
        <template #legend>{{ t('overdraftFacility.label') }}</template>
      </input-radio-group>
    </div>
    <div class="overdraftLimitAmount" v-if="showOverdraftLimit">
      <input-inline>
        <input-money
          id="overdraft-limit"
          name="overdraft-limit"
          v-model="application.overdraftLimit"
          :strict-mode="{ regex: /[^0-9]/g, skipDefault: true }"
          :maxlength="OVERDRAFT_LIMIT_MAX_LENGTH"
          v-validation="[['dynamic', '/overdraftLimit']]"
        >
          <template #label>
            {{ t('overdraftLimit.label') }}
            <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
              <i18n path="statementCollect.overdraftLimit.toolTip" class="tooltip-text"></i18n>
            </popover>
          </template>
          <template #prefix>{{ $t('currency.dollar') }}</template>
        </input-money>
      </input-inline>
      <p class="overdraftLimitHint">{{ t('overdraftFacility.hint') }}</p>
    </div>
    <illion-statement-uploader
      :application="application"
      :updateApplicationWithStore="updateApplicationWithStore"
      @electronicUploadSuccessful="onElectronicUploadSuccessful"
    >
      <template #illion-statement-uploader-header>
        <heading level="4" class="heading headingmargin">
          {{ $t('additionalInfo.bankVerificationHeading') }}
        </heading>
      </template>

      <template #illion-statement-uploader-online-tab-content>
        <div class="electronicVerification">
          <p class="heading">{{ t('electronicVerification.title') }}</p>
          <p class="description" v-for="(simpleStep, index) in simpleSteps" :key="index">
            {{ simpleStep }}
          </p>
          <div class="hintBox"></div>
        </div>
      </template>

      <template #illion-flow-consents>
        <consents
          :pageConsents="bankStatementConsents"
          :consentsPath="consentsPath"
          v-model="application.consents"
        />
      </template>
    </illion-statement-uploader>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from '@/components/Spinner';
import ManualVerification from '@/components/ManualVerification';
import ElectronicVerification from '@/components/ElectronicVerification';
import ebsConsents from '@/modules/ebs-consents';
import CommonStatemnetCollect from '@/views/application/StatementCollect';
import IllionStatementUploader from '@/components/illionComponents/IllionStatementUploader';
import Consents from '@/components/Consents';
import buildTranslators from '@/utils/build-translators';
import applicationMixin from '@/mixins/application';
import uploadFilesMixin from '@/mixins/upload-files';
import bankStatementConsents from '@/modules/bank-statement-consents';
import * as fileStates from '@/modules/upload-service/file-states';
import { OVERDRAFT_LIMIT_MAX_LENGTH } from '@/modules/validation-service/fields-length';
import InputMoney from '@/components/InputMoney';
import InputInline from '@/components/InputInline';
import InputRadioGroup from '@/components/InputRadioGroup';
import mapConstsToOptions from '@/utils/map-consts-to-options';

export default {
  name: 'statement-collect',
  components: {
    IllionStatementUploader,
    Consents,
    InputInline,
    InputMoney,
    InputRadioGroup,
  },
  data() {
    return {
      status: null,
      ebsConsents,
      electronicUploadSuccessful: false,
      showSpinner: false,
      simpleSteps: [
        this.t('electronicVerification.description.firstStep'),
        this.t('electronicVerification.description.secondStep'),
        this.t('electronicVerification.description.thirdStep'),
      ],
      bankStatementConsents,
    };
  },
  computed: {
    ...mapGetters('application', { uiApplicationState: 'getUiStateApplication' }),
    ...mapGetters({
      allFiles: 'file/getAll',
      isUploadInProgress: 'file/isUploadInProgress',
    }),
    consentsPath() {
      return 'statementCollect.electronicVerification.consents';
    },
    uploadedFiles() {
      return this.allFiles.filter(file => file.isUploaded);
    },
    errorFiles() {
      return this.allFiles.filter(
        file =>
          file.documentUploadStatus === fileStates.ERROR ||
          file.documentUploadStatus === fileStates.DELETE_ERROR,
      );
    },
    fileUploadServiceError() {
      return !!this.errorFiles.length;
    },
    someFilesAreUploaded() {
      return this.uploadedFiles.length > 0;
    },
    OVERDRAFT_LIMIT_MAX_LENGTH() {
      return OVERDRAFT_LIMIT_MAX_LENGTH;
    },
    overdraftFacilityOptions() {
      return mapConstsToOptions([true, false], 'statementCollect.overdraftFacility.options');
    },
    showOverdraftLimit() {
      return this.application._extension.haveOverdraftFacility === true;
    },
  },
  watch: {
    'application._extension.haveOverdraftFacility'(newVal) {
      if (newVal === false && this.application.overdraftLimit) {
        this.application.overdraftLimit = null;
      }
    },
  },
  methods: {
    t(key, options) {
      return buildTranslators('statementCollect').t(key, options);
    },
    onElectronicUploadSuccessful() {
      this.electronicUploadSuccessful = true;
      this.$emit('electronicUploadSuccessful');
    },
  },
  extends: CommonStatemnetCollect,
};
</script>
<style lang="sass" scoped>
.overdraftFacilityQuestion
  margin: $glass-spacing-4 0 $glass-spacing-2 0


.tooltip-text
  white-space: pre-line

.popover
  vertical-align: super
  ::v-deep
    .icon
      color: $glass-color-brand-primary-dark-03
    .content
      background: $glass-color-brand-secondary-accent-01
      color: #000000
      z-index: 2
    .iconButton
      &:focus
        outline: 0
        box-shadow: 0 0 0 4px $glass-color-brand-primary-light-02

.overdraftLimitAmount
  margin-bottom: $glass-spacing-6

.overdraftLimitHint
  +font-size(13.33, 20)
  color: $color-system-neutral-gray-5
  font-style: italic
  margin-top: $glass-spacing-1

.headingmargin
  margin-top: $glass-spacing-4
.electronicVerification
  width: 100%
  display: flex
  flex-direction: column
  justify-content: center
  color: $ebs-text-color

  .heading
    +font-size(16, 20)
    font-weight: bold
    line-height: $glass-spacing-3
    margin-top: 0

  .description
    +font-size(13.33, 20)
    margin: 0
    margin-top: $glass-spacing-05
    color: $color-system-neutral-gray-5

  .hintBox
    border-bottom: 1px solid $color-system-neutral-gray-3
    padding-bottom: $glass-spacing-105
    margin: $glass-spacing-2 0 $glass-spacing-2

  .hint
    +font-size(13.33, 16)
    cursor: pointer

    .hintText
      color: $brand-link-partner-color
      text-decoration: underline;
      font-weight: bold

::v-deep
  .brandButton.inputFile
    color: $glass-color-brand-primary-dark-03
    border-color: $glass-color-brand-primary-dark-03

  .tabsButton
    color: $color-system-neutral-black
    &[selected]
      --button-background-color: #0074ff
      --button-border-color: #0074ff
      color: #ffffff
</style>
